import { IconFolder } from "@tabler/icons-react"
import {
  BotIcon,
  Building2,
  CircleHelp,
  Database,
  FileUp,
  Files,
  Workflow,
} from "lucide-react"
import * as React from "react"

import { InfoCircleMenu } from "@/components/common/InfoCircleMenu/InfoCircleMenu"
import { ChatSidebarItem } from "@/components/feature/ChatHistory/ChatSidebarItem"
import { FreeTrialCard } from "@/components/feature/FreeTrial/FreeTrialComposed"
import { UsageTracker } from "@/components/feature/FreeTrial/UsageTracker"
import { SidebarFooterOption } from "@/components/layout/SidebarFooterOption"
import { MaterialComplianceSidebarGroup } from "@/components/MaterialComplianceSidebarGroup"
import { SidebarLogo } from "@/components/SidebarLogo"
import { Separator } from "@/components/ui/separator"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { MC_VIEW_PERMISSIONS } from "@/constants/permissions"
import {
  useFeatureFlag,
  useGetAllFeatureFlags,
} from "@/contexts/FeatureFlagContext"
import { HELP_BASE } from "@/features/helpdesk"
import { useOnboarding } from "@/features/onboarding"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { useGetCompanyProfileStatus } from "@/services/api/companyInfoService"
import { IconSettings } from "@tabler/icons-react"
import { useLocation, useNavigate } from "react-router"
import { LockedModuleSidebarItem } from "./LockedModuleSidebarItem"
import { resolveSidebarNav, type SidebarIconKey } from "./resolveSidebarNav"
import { SidebarUserMenu } from "./SidebarUserMenu"
import { UpsellCard } from "./UpsellCard"
import { UpsellDialog } from "./UpsellDialog"

const ICONS: Record<
  SidebarIconKey,
  React.ComponentType<{ style?: React.CSSProperties }>
> = {
  upload: FileUp,
  history: Files,
  database: Database,
  autoPilots: Workflow,
  folder: IconFolder,
}

export function AppSidebarV2({
  isSidebarOpen,
  isSidebarLockedOpen = false,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  isSidebarOpen?: boolean
  isSidebarLockedOpen?: boolean
}) {
  const { translate } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { setOpen, setOpenMobile } = useSidebar()
  const flags = useGetAllFeatureFlags()
  const chatHistoryEnabled = useFeatureFlag("CHAT_HISTORY")
  const helpdeskEnabled = useFeatureFlag("HELPDESK")
  const { handleHighlightedAction } = useOnboarding()
  const { data: companyProfileStatus } = useGetCompanyProfileStatus({
    enabled: flags.COMPANY_PROFILE,
  })

  const { hasPermission } = useUser()
  // The parent "Material Compliance" group appears only if the member can view
  // at least ONE of its studios. Sub-tabs then filter independently inside
  // MaterialComplianceSidebarGroup. hasPermission is kill-switch-aware —
  // TEAM_MANAGEMENT off → all pass → group always shows (backward compat).
  const showMcGroup = React.useMemo(
    () => MC_VIEW_PERMISSIONS.some((p) => hasPermission(p)),
    [hasPermission],
  )
  const model = React.useMemo(() => {
    const raw = resolveSidebarNav(flags)
    return {
      ...raw,
      // Filter entries by permission. Item + chat entries carry their own
      // `requiresPermission`; the mcGroup is filtered by showMcGroup above.
      entries: raw.entries.filter((e) => {
        if (e.kind === "mcGroup") return showMcGroup
        if (e.kind !== "item" && e.kind !== "chat") return true
        const perm =
          "requiresPermission" in e ? e.requiresPermission : undefined
        return !perm || hasPermission(perm)
      }),
    }
  }, [flags, hasPermission, showMcGroup])

  const [upsellSource, setUpsellSource] = React.useState<
    "locked_entry" | "upsell_card" | null
  >(null)
  const openUpsell = (source: "locked_entry" | "upsell_card") => {
    if (!model.upsell) return
    trackEvent(AnalyticsEvent.UPGRADE_MODAL_SHOWN, {
      source: `sidebar_${source}`,
      product: model.upsell,
    })
    setUpsellSource(source)
  }

  const handleNavigation = (path: string, elementId?: string) => {
    if (elementId) handleHighlightedAction(elementId)
    navigate(path)
  }

  // Collapse the sidebar when the user enters the material-compliance section
  // from elsewhere. The app shell persists across navigation, so this runs on
  // real route changes (it used to be reset by a remount on every navigation).
  const prevPathnameRef = React.useRef(pathname)
  React.useEffect(() => {
    const prev = prevPathnameRef.current
    prevPathnameRef.current = pathname

    const wasInSection =
      prev === "/material-compliance" ||
      prev.startsWith("/material-compliance/")
    const isInSection =
      pathname === "/material-compliance" ||
      pathname.startsWith("/material-compliance/")

    if (!isInSection || wasInSection || isSidebarLockedOpen) {
      return
    }

    setOpen(false)
    setOpenMobile(false)
  }, [pathname, isSidebarLockedOpen, setOpen, setOpenMobile])

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarLogo />
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {model.entries.map((entry) => {
              switch (entry.kind) {
                case "divider":
                  return <Separator key={entry.id} className="my-2" />
                case "chat":
                  return chatHistoryEnabled ? (
                    <ChatSidebarItem
                      key={entry.id}
                      isSidebarOpen={isSidebarOpen}
                      rowClickToggles
                    />
                  ) : (
                    <SidebarFooterOption
                      key={entry.id}
                      id="navItem4"
                      title={translate("AI_CHAT/ASSISTANT")}
                      icon={BotIcon}
                      onClick={() => handleNavigation("/chat", "navItem4")}
                    />
                  )
                case "mcGroup":
                  return (
                    <MaterialComplianceSidebarGroup
                      key={entry.id}
                      title={translate("NAV_MATERIAL_COMPLIANCE")}
                    />
                  )
                case "lockedMc":
                  return (
                    <LockedModuleSidebarItem
                      key={entry.id}
                      product="mc"
                      onOpenUpsell={() => openUpsell("locked_entry")}
                    />
                  )
                case "lockedQuestionnaire":
                  return (
                    <LockedModuleSidebarItem
                      key={entry.id}
                      product="questionnaire"
                      onOpenUpsell={() => openUpsell("locked_entry")}
                    />
                  )
                case "lockedOnboarding":
                  return (
                    <LockedModuleSidebarItem
                      key={entry.id}
                      variant="onboarding"
                      id={entry.id}
                      label={translate(entry.labelKey)}
                      icon={ICONS[entry.iconKey]}
                    />
                  )
                case "companyInfo": {
                  if (!companyProfileStatus?.hasProfile) return null
                  return (
                    <SidebarFooterOption
                      key={entry.id}
                      id={entry.id}
                      title={translate("COMPANY_INFO")}
                      icon={Building2}
                      onClick={() =>
                        handleNavigation("/company-info", entry.id)
                      }
                      showDot={companyProfileStatus?.state === "ready"}
                    />
                  )
                }
                case "item": {
                  const Icon = ICONS[entry.iconKey]
                  return (
                    <SidebarFooterOption
                      key={entry.id}
                      id={entry.id}
                      title={translate(entry.labelKey)}
                      icon={Icon}
                      onClick={() => handleNavigation(entry.path, entry.id)}
                    />
                  )
                }
              }
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="pb-4">
        {helpdeskEnabled ? (
          <SidebarFooterOption
            id="navHelp"
            title={translate("SIDEBAR/HELP")}
            icon={CircleHelp}
            onClick={() => navigate(HELP_BASE)}
          />
        ) : null}
        <InfoCircleMenu variant="sidebar" />
        <Separator />
        {model.upsellCard !== null && isSidebarOpen ? (
          <UpsellCard
            product={model.upsellCard}
            onOpenUpsell={() => openUpsell("upsell_card")}
          />
        ) : null}
        {isSidebarOpen ? <FreeTrialCard.DaysLimited /> : null}
        {isSidebarOpen ? <UsageTracker /> : null}
        <SidebarMenuItem>
          <SidebarMenuButton
            id="navSettings"
            onClick={() => navigate("/settings")}
            tooltip={{
              children: translate("SETTINGS/TITLE"),
              side: "right",
              align: "center",
              className:
                "bg-secondary-foreground text-card shadow-md border border-muted-foreground",
            }}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center group-data-[collapsible=icon]:h-5 group-data-[collapsible=icon]:w-5">
              <IconSettings className="h-5 w-5" />
            </span>
            <span className="truncate">{translate("SETTINGS/TITLE")}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarUserMenu />
      </SidebarFooter>
      {!isSidebarLockedOpen && <SidebarRail />}
      <UpsellDialog
        open={upsellSource !== null}
        onOpenChange={(open) => !open && setUpsellSource(null)}
        source={upsellSource ?? "locked_entry"}
        product={model.upsell ?? "mc"}
      />
    </Sidebar>
  )
}
